import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react"
import PropTypes from "prop-types"
import "stylesheets/widgets/Dashboard/ChartNode.scss"
import DottedButton from "components/widgets/Dashboard/OrganizationChart/DottedButton"
import NodeOrganization from "services/organizations/NodeOrganization"
import ChartNodeChildrenMolecule from "views/molecules/organization/ChartNodesChildrenMolecule"
import { COLORS } from "utils/constants/colors"
import NodeAction from "components/widgets/Dashboard/OrganizationChart/NodeActions"
import ChartNodeHeaderMolecule from "views/molecules/organization/ChartNodeHeaderMolecule"
import ChartElementBodyAtom from "views/molecules/organization/ChartElementBodyAtom"
import { useAppContext } from "components/AppContext"
import ChartNodeOrganizationsMolecule from "views/molecules/organization/ChartOrganizationsMolecule"
import { ORGANIZATION_CATEGORY_TO_COLOR } from "utils/constants/organization/nodeTypes"

const NUMBER_OF_LEAVES_BEFORE_COLUMN = 3

const ChartNodeMolecule = forwardRef(
  // eslint-disable-next-line no-unused-vars
  ({ node, onCloseSiblings, isParentColumn }, ref) => {
    const {
      currentNode,
      currentOrganization,
      isTreeNodeExpanded,
      setTreeNodeExpanded,
    } = useAppContext()
    const isPersistedExpanded = isTreeNodeExpanded(node.id)
    const isGuestOrg = !!currentOrganization?.is_partial_partner
    const userToggledRef = useRef(false)

    const [isChildrenBottomCollapsed, setIsChildrenBottomCollapsed] = useState(
      () => {
        if (isPersistedExpanded) return false
        if (isGuestOrg) return false
        return !node.isCurrentNodeBelow
      }
    )
    const [isChildrenRightCollapsed, setIsChildrenRightCollapsed] = useState(
      true
    )
    const [childrenPosition, setChildrenPosition] = useState("bottom")

    useEffect(() => {
      userToggledRef.current = false
    }, [node.id, currentNode?.id])

    useEffect(() => {
      if (userToggledRef.current || !isPersistedExpanded) return

      setIsChildrenBottomCollapsed(false)
      setIsChildrenRightCollapsed(true)
    }, [node.id, isPersistedExpanded])

    useEffect(() => {
      if (userToggledRef.current || !node.isCurrentNodeBelow) return

      const isScopeIdOnBottom = node.children?.find(
        (child) =>
          child.isCurrentNodeBelow ||
          NodeOrganization.isNodeCurrentScope(child, currentNode)
      )

      if (isScopeIdOnBottom) {
        onCloseSiblings?.()
        setIsChildrenBottomCollapsed(false)
        setChildrenPosition("bottom")
      } else if (node.organizations?.length > 0) {
        onCloseSiblings?.()
        setIsChildrenRightCollapsed(false)
        setChildrenPosition("right")
        setIsChildrenBottomCollapsed(true)
      }
      // onCloseSiblings is intentionally omitted: it is recreated every render
      // and would cause an infinite update loop if included in deps.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [node.id, currentNode?.id, node.isCurrentNodeBelow])

    const nodeClass = () =>
      NodeOrganization.getNodeClass(
        node,
        !isChildrenBottomCollapsed,
        NodeOrganization.isNodeCurrentScope(node, currentNode),
        !isChildrenRightCollapsed,
        node.discarded || node.parent_discarded
      )

    const toggleChildrenViewRight = () => {
      userToggledRef.current = true

      if (isChildrenRightCollapsed) {
        onCloseSiblings?.()
        setChildrenPosition("right")
        setIsChildrenRightCollapsed(false)
        setIsChildrenBottomCollapsed(true)
        setTreeNodeExpanded(node.id, true)
      } else {
        setIsChildrenRightCollapsed(true)
        setIsChildrenBottomCollapsed(true)
        setTreeNodeExpanded(node.id, false)
      }
    }

    const toggleChildrenBottomView = () => {
      userToggledRef.current = true

      if (isChildrenBottomCollapsed) {
        onCloseSiblings?.()
        setChildrenPosition("bottom")
        setIsChildrenBottomCollapsed(false)
        setIsChildrenRightCollapsed(true)
        setTreeNodeExpanded(node.id, true)
      } else {
        setIsChildrenBottomCollapsed(true)
        setIsChildrenRightCollapsed(true)
        setTreeNodeExpanded(node.id, false)
      }
    }

    useImperativeHandle(ref, () => ({
      closeChildren() {
        setIsChildrenRightCollapsed(true)
        setIsChildrenBottomCollapsed(true)
        setTreeNodeExpanded(node.id, false)
      },
    }))

    const hasOrganizations = node.organizations?.length >= 1
    const hasChildren = node.children?.length >= 1

    const childrenColor =
      ORGANIZATION_CATEGORY_TO_COLOR[node.organization_category] ||
      COLORS.social.c30

    const classNames = [
      "oc-hierarchy",
      childrenPosition,
      hasOrganizations ? "children-right" : "",
      hasChildren ? "children-bottom" : "",
      node.organization_category,
      !isChildrenRightCollapsed ? "children-right-expanded" : "",
    ].join(" ")

    const manyChildrenOnlyLeaves = () =>
      node.children?.length > NUMBER_OF_LEAVES_BEFORE_COLUMN &&
      node.children.every(
        (child) => !child.children || child.children.length === 0
      )

    return (
      <li className={classNames}>
        <div className={nodeClass()}>
          <ChartElementBodyAtom
            progress={node.progress}
            actionComponent={<NodeAction node={node} />}
            headerComponent={<ChartNodeHeaderMolecule node={node} />}
            isPartialPartner={currentOrganization?.is_partial_partner}
          />

          {hasChildren && (
            <DottedButton
              onClick={toggleChildrenBottomView}
              position="bottom"
              color={childrenColor}
            />
          )}

          {hasOrganizations && (
            <DottedButton
              onClick={toggleChildrenViewRight}
              position="right"
              color={COLORS.danger.dark}
            />
          )}
        </div>

        <ChartNodeChildrenMolecule
          node={node}
          position="bottom"
          isParentColumn={manyChildrenOnlyLeaves()}
          hidden={isChildrenBottomCollapsed}
        />

        <ChartNodeOrganizationsMolecule
          node={node}
          position="right"
          hidden={isChildrenRightCollapsed}
        />
      </li>
    )
  }
)

ChartNodeMolecule.displayName = "ChartNodeMolecule"

ChartNodeMolecule.propTypes = {
  node: PropTypes.object.isRequired,
  onCloseSiblings: PropTypes.func,
  isParentColumn: PropTypes.bool,
}

ChartNodeMolecule.defaultProps = {
  onCloseSiblings: () => {},
  isParentColumn: false,
}

export default ChartNodeMolecule
